import { find, isString } from 'lodash';

import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import MenuItem from '@material-ui/core/MenuItem';
import withStyles from '@material-ui/core/styles/withStyles';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import { useTranslation } from 'react-i18next';
import Divider from '@material-ui/core/Divider';
import customSelectStyle from '../customSelectStyle';

export const getOptionLabelHtml = (i, additional, classes) => <div className={classes.ellipsis}>
    <span className={classes.companyName}>{i.name}</span>
    <div className={classes.mailBlock}>{i.email}</div>
    { additional?.name && <span className={classes.companyBlock}>{additional.name}</span> }
</div>;

function CustomSelect({
    testId,
    classes,
    formControlProps,
    labelText,
    id,
    labelProps,
    selectProps,
    error,
    success,
    useOptionsAsObject,
    options,
    value,
    onChange,
    onClick,
    renderOptions,
    placeholder,
    disabled,
    noBorder,
    displayEmpty,
}) {
    const { t } = useTranslation();
    const labelClasses = clsx({
        [` ${classes.label}`]: true,
        [` ${classes.labelRootError}`]: error,
        [` ${classes.labelRootSuccess}`]: success && !error,
    });

    const rootClasses = clsx({
        [classes.marginTop]: labelText === undefined,
        [classes.root]: true,
    });

    const selectClasses = clsx({
        [classes.withoutLabel]: !labelText,
        [classes.select]: true,
        [classes.noBorder]: noBorder,
    });

    const selectFormControlClasses = clsx({
        [classes.selectFormControl]: true,
        [classes.selectFormControlError]: error,
        [classes.selectFormControlNoBorder]: noBorder,
    });

    const resultOptions = placeholder
        ? [
            {
                value: placeholder,
                label: placeholder,
                disabled,
            },
            ...options,
        ] : options;

    const resultValue = useOptionsAsObject
        ? find(resultOptions, { value }) || find(resultOptions, { value: placeholder })
        : value ?? placeholder;

    return (
        <FormControl
            {...formControlProps}
            className={`${formControlProps.className} ${selectFormControlClasses}`}
        >
            {labelText ? (
                <InputLabel
                    data-test-id='input-label'
                    className={classes.selectLabel + labelClasses}
                    classes={{
                        shrink: classes.selectLabelShrink,
                    }}
                    htmlFor={id}
                    {...labelProps}
                >
                    {labelText}
                </InputLabel>
            ) : null}
            <Select
                classes={{
                    root: rootClasses,
                    select: selectClasses,
                    disabled: classes.disabled,
                    selectMenu: classes.selectMenu,
                }}
                MenuProps={{ classes: { paper: classes.paper } }}
                id={id}
                onChange={onChange}
                value={resultValue}
                data-test-id={testId}
                disabled={disabled}
                onClick={onClick}
                {...selectProps}
                displayEmpty={displayEmpty}
            >
                {(() => {
                    if (typeof renderOptions === 'function') {
                        return renderOptions({
                            options: resultOptions,
                            className: classes.selectMenuItem,
                        });
                    }

                    return (resultOptions || []).map((option, index) => {
                        if (option.divider) {
                            return (
                                <Divider variant="fullWidth"
                                    orientation="horizontal" key={index}
                                    style={{ margin: 5 }}
                                />
                            );
                        }

                        return <MenuItem
                            data-test-id={`${testId}-${option.value}`}
                            value={useOptionsAsObject ? find(resultOptions, { value: option.value }) : option.value }
                            key={index}
                            focusVisibleClassName="selected"
                            disabled={option.disabled}
                            classes={{
                                root: classes.selectMenuItem,
                            }}
                        >
                            {
                                isString(option.label)
                                    ? t(option.label)
                                    : option.label
                            }
                        </MenuItem>;
                    });
                })()}
            </Select>
        </FormControl>
    );
}

CustomSelect.defaultProps = {
    testId: 'select',
    classes: { paper: '' },
    formControlProps: {},
    disabled: false,
    placeholder: '',
    labelText: '',
    id: '',
    labelProps: {},
    selectProps: {},
    noBorder: false,
    useOptionsAsObject: false,
};

CustomSelect.propTypes = {
    testId: PropTypes.string,
    classes: PropTypes.object.isRequired,
    labelText: PropTypes.node,
    labelProps: PropTypes.object,
    id: PropTypes.string,
    placeholder: PropTypes.string,
    selectProps: PropTypes.object,
    formControlProps: PropTypes.object,
    error: PropTypes.bool,
    success: PropTypes.bool,
    disabled: PropTypes.bool,
    children: PropTypes.node,
    options: PropTypes.array,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.array,
        PropTypes.bool,
    ]),
    onChange: PropTypes.func,
    onClick: PropTypes.func,
    noBorder: PropTypes.bool,
    renderOptions: PropTypes.func,
    displayEmpty: PropTypes.bool,
    useOptionsAsObject: PropTypes.bool,
};

export default withStyles(customSelectStyle)(CustomSelect);
